import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import Button from "@mui/material/Button";
import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import RightworkSelect from "../../../../../components/shared/RightworkSelect";
import { downloadCSV, objectToCSV } from "../../../../../utilities/csvExport";
import PrebuiltReportShell from "../PrebuiltReportShell";
import { getEligibleStoresForReport } from "../reportAccess";
import { lastCompletedBusinessDay } from "./_shared/reportDates";
import { flagExportRows, loadFlagReport, rangeError } from "./_shared/laborFlags";

const PAGE_SIZE = 100;
const actionSx = { textTransform: "none" };
// Match the shared select's dimensions, labels, neutral border and focus states.
const datePickerSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 42, backgroundColor: "white", borderRadius: "8px", fontSize: "14px", color: "#334155",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#94a3b8" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "error.main" },
    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "action.disabled" },
  },
};

function Filter({ id, label, children }) {
  return <div className="w-[240px] sm:w-full">
    <label id={`${id}-label`} htmlFor={id} className="block mb-1 text-xs font-medium text-slate-600">{label}</label>
    {children}
  </div>;
}

function FlagTable({ headers, rows }) {
  return <div className="overflow-x-auto rounded-lg border border-slate-200">
    <table className="w-full min-w-[640px] text-left text-sm">
      <thead className="bg-slate-50 text-slate-600"><tr>{headers.map((h) => <th scope="col" key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-slate-100">{rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j} className={`px-4 py-3 align-top text-slate-700 ${j < row.length - 1 ? "whitespace-nowrap" : ""}`}>{cell}</td>)}</tr>)}</tbody>
    </table>
  </div>;
}

export default function LaborFlagsReport({ report, onBack, homeStore, homeCompany, companyContext = homeCompany, storeNames, storeOptions }) {
  // Require a loaded membership list: never issue requests during the
  // registry's intentional initial-loading fallback.
  const eligible = storeOptions?.length ? getEligibleStoresForReport(report, storeNames, storeOptions) : [];
  const companyId = Number(companyContext);
  const stores = eligible.filter((s) => Number(s.company) === companyId).sort((a, b) => a.store_name.localeCompare(b.store_name));
  const [locationPick, setLocationPick] = useState(null);
  const locationIds = locationPick?.company === companyId ? locationPick.ids : null;
  const selectedStores = locationIds === null ? stores : stores.filter((s) => locationIds.includes(s.id));
  const [start, setStart] = useState(() => dayjs(lastCompletedBusinessDay(storeNames, homeStore)).subtract(6, "day"));
  const [end, setEnd] = useState(() => dayjs(lastCompletedBusinessDay(storeNames, homeStore)));
  const [preset, setPreset] = useState("custom");
  const [view, setView] = useState("summary");
  const [page, setPage] = useState(0);
  const [request, setRequest] = useState(null);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const workQueue = useRef(Promise.resolve());
  const validation = rangeError(start, end) || (!selectedStores.length ? "Select at least one eligible location." : null);
  const filterKey = JSON.stringify({ companyId, stores: selectedStores.map((s) => [s.id, s.store_name]), start: start?.format("YYYY-MM-DD"), end: end?.format("YYYY-MM-DD") });
  const current = !validation && result?.key === filterKey ? result.data : null;
  const pending = loading && request?.key === filterKey;

  useEffect(() => {
    let cancelled = false;
    if (!request) return undefined;
    if (request.key !== filterKey) {
      setRequest(null);
      setResult(null);
      setError(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setProgress("Preparing audit…");
    setResult(null);
    setError(null);
    setPage(0);
    // A cancelled request may still be executing. Serialize explicit reruns
    // behind it, and discard late results or superseded queued work.
    const task = workQueue.current.then(() => loadFlagReport(request.stores, request.start, request.end, {
      isCancelled: () => cancelled,
      onProgress: (message) => { if (!cancelled) setProgress(message); },
    }));
    workQueue.current = task.catch(() => {});
    task.then((data) => { if (!cancelled) setResult({ key: request.key, data }); })
      .catch(() => { if (!cancelled) setError({ key: request.key, message: "Couldn't load all selected locations. No partial results are shown." }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [request, filterKey]);

  function run() {
    if (validation || pending) return;
    setRequest({ key: filterKey, stores: selectedStores, start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") });
  }

  function choosePreset(value) {
    setPreset(value);
    if (value === "custom") return;
    const today = dayjs(lastCompletedBusinessDay(storeNames, selectedStores[0]?.id || homeStore));
    if (value === "last30") { setStart(today.subtract(29, "day")); setEnd(today); return; }
    if (value === "lastYear") { setStart(today.subtract(1, "year").startOf("year")); setEnd(today.subtract(1, "year").endOf("year")); return; }
    if (value === "year") { setStart(today.startOf("year")); setEnd(today); return; }
    const quarterStart = today.month(Math.floor(today.month() / 3) * 3).startOf("month");
    setStart(value === "lastQuarter" ? quarterStart.subtract(3, "month") : quarterStart);
    setEnd(value === "lastQuarter" ? quarterStart.subtract(1, "day") : today);
  }

  const filters = <div className="flex flex-wrap items-end gap-4">
    <Filter id="flags-locations" label="Select locations"><RightworkSelect id="flags-locations" multiple value={selectedStores.map((s) => s.id)} options={stores.map((s) => ({ value: s.id, label: s.store_name }))} renderValue={(ids) => `${ids.length} locations selected`} onChange={(e) => setLocationPick({ company: companyId, ids: e.target.value })} /></Filter>
    <Filter id="flags-period" label="Period"><RightworkSelect id="flags-period" value={preset} onChange={(e) => choosePreset(e.target.value)} options={[{ value: "custom", label: "Custom dates" }, { value: "last30", label: "Last 30 completed days" }, { value: "quarter", label: "Quarter to date" }, { value: "lastQuarter", label: "Last quarter" }, { value: "year", label: "Year to date" }, { value: "lastYear", label: "Last year" }]} /></Filter>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Filter id="flags-start-date" label="Start date">
        <DesktopDatePicker value={start} onChange={(d) => { setPreset("custom"); setStart(d); }} sx={datePickerSx} slotProps={{ textField: { id: "flags-start-date", size: "small" } }} />
      </Filter>
      <Filter id="flags-end-date" label="End date">
        <DesktopDatePicker value={end} onChange={(d) => { setPreset("custom"); setEnd(d); }} sx={datePickerSx} slotProps={{ textField: { id: "flags-end-date", size: "small" } }} />
      </Filter>
    </LocalizationProvider>
    <Button variant="outlined" sx={{ ...actionSx, height: 42 }} disabled={!!validation || pending} onClick={run}>Run report</Button>
    {pending && <Button sx={actionSx} onClick={() => { setRequest(null); setLoading(false); }}>Cancel</Button>}
  </div>;
  const exportRows = current ? flagExportRows(current, view) : [];
  const actions = <Button variant="outlined" size="small" sx={actionSx} disabled={!current || pending || !exportRows.length} onClick={() => downloadCSV(objectToCSV(exportRows), `Labor_Flags_${view}_${start.format("YYYY-MM-DD")}_${end.format("YYYY-MM-DD")}.csv`)}>Download {view === "summary" ? "summary" : "detail"} CSV</Button>;
  const pageCount = Math.max(1, Math.ceil((current?.details.length || 0) / PAGE_SIZE));
  const visiblePage = Math.min(page, pageCount - 1);

  return <PrebuiltReportShell report={report} onBack={onBack} filters={filters} actions={actions}>
    {validation && <p role="alert" className="mb-4 text-sm text-amber-800">{validation}</p>}
    {pending && <p role="status" className="rounded-lg bg-violet-50 p-4 text-sm text-violet-800">{progress || "Checking completed time entries…"} Large date ranges can take several minutes.</p>}
    {!pending && error?.key === filterKey && <p role="alert" className="rounded-lg bg-amber-50 p-4 text-sm text-amber-800">{error.message} <Button sx={actionSx} onClick={run}>Retry</Button></p>}
    {!pending && !current && error?.key !== filterKey && <p className="rounded-lg border border-slate-200 p-6 text-sm text-slate-500">Choose locations and dates, then run the report.</p>}
    {!pending && current && <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div><h4 className="font-semibold text-slate-900">{current.total} actual labor {current.total === 1 ? "flag" : "flags"}</h4><p className="text-xs text-slate-500">{stores[0]?.company_info?.company_name || stores[0]?.company_name} · {start.format("MMM D, YYYY")} – {end.format("MMM D, YYYY")} · {selectedStores.length} locations</p></div>
        <div className="flex gap-1 prebuilt-report-no-print" role="group" aria-label="Report view">{["summary", "detail"].map((v) => <Button key={v} variant={view === v ? "contained" : "outlined"} aria-pressed={view === v} onClick={() => setView(v)} sx={{ ...actionSx, ...(view === v ? { bgcolor: "primary.main", color: "primary.contrastText", "&:hover": { bgcolor: "primary.dark" } } : {}) }}>{v === "summary" ? "Summary" : "Detail"}</Button>)}</div>
      </div>
      {current.skipped.length > 0 && <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">Not evaluated from time entries: {current.skipped.map((s) => `${s.location}: ${s.type}`).join("; ")}. Zero flags does not establish compliance.</p>}
      {!current.total && <p className="mb-4 text-sm text-slate-600">No flags found in completed, mapped time entries for this range.</p>}
      {view === "summary" ? <FlagTable headers={["Location", ...current.types.map((t) => t[1]), "Total flags"]} rows={[...current.summary.map((s) => [s.location, ...current.types.map(([id]) => s.counts[id] || 0), s.total]), ["Total", ...current.types.map(([id]) => current.summary.reduce((sum, s) => sum + (s.counts[id] || 0), 0)), current.total]]} /> : <>
        <FlagTable headers={["Location", "Date", "Employee", "Flag type", "Description"]} rows={current.details.slice(visiblePage * PAGE_SIZE, (visiblePage + 1) * PAGE_SIZE).map((r) => [r.location, r.date, r.employee, r.type, r.detail])} />
        {pageCount > 1 && <nav aria-label="Detail pages" className="flex items-center gap-2 mt-3 prebuilt-report-no-print"><span className="sr-only" aria-live="polite">Page {visiblePage + 1} of {pageCount}</span><Button sx={actionSx} disabled={visiblePage === 0} onClick={() => setPage(visiblePage - 1)}>Previous</Button><Button sx={actionSx} disabled={visiblePage + 1 === pageCount} onClick={() => setPage(visiblePage + 1)}>Next</Button></nav>}
      </>}
    </>}
  </PrebuiltReportShell>;
}
